import React, { useState, useRef, useEffect } from 'react'

interface SearchableDropdownProps {
	options: string[]
	value: string
	onChange: (value: string) => void
	placeholder?: string
	loading?: boolean
	disabled?: boolean
	className?: string
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Search and select...',
	loading = false,
	disabled = false,
	className = '',
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const [highlightedIndex, setHighlightedIndex] = useState(-1)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLUListElement>(null)

	// Filter options based on search term
	const filteredOptions = options.filter((option) =>
		option.toLowerCase().includes(searchTerm.toLowerCase())
	)

	// Get display value
	const displayValue = value || searchTerm

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
				setSearchTerm('')
				setHighlightedIndex(-1)
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault()
				if (!isOpen) {
					setIsOpen(true)
				} else {
					setHighlightedIndex((prev) =>
						prev < filteredOptions.length - 1 ? prev + 1 : prev
					)
				}
				break
			case 'ArrowUp':
				e.preventDefault()
				if (isOpen) {
					setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
				}
				break
			case 'Enter':
				e.preventDefault()
				if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
					handleSelect(filteredOptions[highlightedIndex])
				} else if (isOpen && filteredOptions.length === 1) {
					handleSelect(filteredOptions[0])
				}
				break
			case 'Escape':
				setIsOpen(false)
				setSearchTerm('')
				setHighlightedIndex(-1)
				break
		}
	}

	// Scroll highlighted item into view
	useEffect(() => {
		if (isOpen && highlightedIndex >= 0 && listRef.current) {
			const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement
			if (highlightedElement) {
				highlightedElement.scrollIntoView({
					block: 'nearest',
					behavior: 'smooth',
				})
			}
		}
	}, [highlightedIndex, isOpen])

	const handleSelect = (option: string) => {
		onChange(option)
		setIsOpen(false)
		setSearchTerm('')
		setHighlightedIndex(-1)
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchTerm = e.target.value
		setSearchTerm(newSearchTerm)
		setIsOpen(true)
		setHighlightedIndex(-1)

		// If exact match, select it
		const exactMatch = options.find(
			(opt) => opt.toLowerCase() === newSearchTerm.toLowerCase()
		)
		if (exactMatch) {
			onChange(exactMatch)
		} else if (newSearchTerm === '') {
			onChange('')
		}
	}

	const handleInputFocus = () => {
		if (!disabled) {
			setIsOpen(true)
		}
	}

	const handleInputClick = () => {
		if (!disabled) {
			setIsOpen(true)
		}
	}

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation()
		onChange('')
		setSearchTerm('')
		setIsOpen(false)
		inputRef.current?.focus()
	}

	return (
		<div ref={dropdownRef} className={`relative ${className}`}>
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					value={isOpen ? searchTerm : displayValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onClick={handleInputClick}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={disabled || loading}
					className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-8 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-slate-50 disabled:text-slate-400"
				/>
				{value && !disabled && (
					<button
						type="button"
						onClick={handleClear}
						className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
						aria-label="Clear selection"
					>
						<svg
							className="h-4 w-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				)}
				{loading && (
					<div className="absolute right-2 top-1/2 -translate-y-1/2">
						<svg
							className="h-4 w-4 animate-spin text-slate-400"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							/>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							/>
						</svg>
					</div>
				)}
			</div>

			{isOpen && !disabled && (
				<div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
					<ul
						ref={listRef}
						className="max-h-60 overflow-auto py-1 text-sm"
						role="listbox"
					>
						{loading ? (
							<li className="px-3 py-2 text-slate-500">Loading...</li>
						) : filteredOptions.length === 0 ? (
							<li className="px-3 py-2 text-slate-500">No options found</li>
						) : (
							filteredOptions.map((option, index) => (
								<li
									key={option}
									onClick={() => handleSelect(option)}
									onMouseEnter={() => setHighlightedIndex(index)}
									className={`cursor-pointer px-3 py-2 ${
										highlightedIndex === index
											? 'bg-primary-50 text-primary-700'
											: value === option
											? 'bg-slate-50 text-slate-900'
											: 'text-slate-700 hover:bg-slate-50'
									}`}
									role="option"
									aria-selected={value === option}
								>
									{option}
								</li>
							))
						)}
					</ul>
				</div>
			)}
		</div>
	)
}

